import MainSlide from './MainSlide';
import { useEffect, useRef, useState, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { GlobalContext } from '../contexts/GlobalContext';
import { CICollaboration, CIHandShake, CISearchEngine, CIAnalysis, CIPortal, CIMedicalInsurance, CITrophy, CISuccess, CISearch, CIResources } from './CoazIcons';
import Button from '../components/Button';
import { MdSubdirectoryArrowRight } from 'react-icons/md';
import { IoIosArrowRoundForward } from 'react-icons/io';
import client from '../client';
import Section from './Section';
import NewsItems from './NewsItems';
import { X_PADDING } from '../constants';

const scrollCallbacks = [];

const Home = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);

  const { getCurrent } = client;

  useEffect(() => {
    window.addEventListener('scroll', function (event) {
      for (let callback of scrollCallbacks) {
        if (callback) {
          callback(event);
        }
      }
    });

    getCurrent()
      .then((response) => {
        if (response && response.user) {
          setUser(response.user);
        }
      })
      .catch(() => { });
  }, []);

  return (
    <>
      <section>
        <div className='relative flex flex-col w-full h-auto bg-[rgb(243,244,245)]'>
          <MainSlide />
        </div>
      </section>
      <section className='pt-[112px]'>
        <News />
      </section>
      <section>
        <Features />
      </section>
      <section>
        <Statistics />
      </section>
      <section className='pt-[112px]'>
        <Services />
      </section>
      <section className='pt-[112px]'>
        <ContactUs />
      </section>
      <section className='pt-[112px]'>
        <MemberBenefits />
      </section>
      <section>
        {!user &&
          <Register />
        }
      </section>
    </>
  );
};

export default Home;

const Features = () => {
  return (
    <div className={`${X_PADDING} w-full mx-auto py-16`}>
      <div className="flex flex-col md:flex-row w-full space-y-8 md:space-y-0 md:space-x-8 h-fit">
        <div className='flex flex-col w-full md:w-[60%] h-fit space-y-12'>
          <div className='flex flex-col w-full h-fit space-y-8'>
            <p className='font-nunitoSansBold text-[32px] text-heading'>Our Main Features</p>
            <p className='w-full h-fit text-gray font-nunitoSansRegular'>
              COAZ is dedicated to empowering clinical officers through robust governance, strategic advocacy, and professional development. Our core features drive excellence in healthcare delivery and strengthen the clinical profession in Zambia.
            </p>
          </div>
          <div className='grid grid-cols-1 lg:grid-cols-2 w-full h-fit gap-6 overflow-hidden'>
            <Feature
              Icon={CICollaboration}
              title='Organization'
              description='Strong leadership and governance that represent and advance Clinical Officers across Zambia.'
              active={true}
            />
            <Feature
              Icon={CISearchEngine}
              title='Knowledge Centre'
              description="Access trusted clinical resources, guidelines, and professional information."
            />
            <Feature
              Icon={CIAnalysis}
              title='Health Analysis'
              description="COAZ addresses health disparities within the clinical workforce to ensure equitable healthcare for all Zambians."
            />
            <Feature
              Icon={CIHandShake}
              title='Strategic Partnerships'
              description="Building strong partnerships to advance healthcare, education, and professional development."
              
            />
          </div>
        </div>
        <SuccessStory
          paragraphs={[
            `Since its establishment in 1967, the Clinical Officers Association of Zambia has grown into the nation's leading professional body representing Clinical Officers through advocacy, leadership, education, and professional development.`,

            `Today, COAZ continues to shape healthcare policy, strengthen clinical practice, champion members' interests, and improve access to quality healthcare while building the next generation of healthcare leaders.`
          ]}
        />
      </div>
    </div>
  );
};

const Feature = ({ Icon, title, description, active }) => {
  const [highlighted, setHighlighted] = useState(false);

  return (
    <div className="w-full h-64">
      <div
        onMouseEnter={() => setHighlighted(true)}
        onMouseLeave={() => setHighlighted(false)}
        className={`feature ${active ? 'active' : ''} flex flex-col space-y-2 items-center p-[10%] hover:text-white overflow-hidden rounded-lg transition-all duration-300 ${active || highlighted ? 'bg-[#1477c2] text-white shadow-md' : 'bg-[#eef7fd] text-[#16365c]'
          }`}
      >
        {Icon && <div className='flex shrink-0'><Icon size={48} fill={active || highlighted ? 'white' : 'rgb(27,156,227)'} /></div>}
        {title &&
          <p style={{ transition: 'color .4s ease-in-out' }}
            className={`text-[20px] text-center font-nunitoSansBold ${active || highlighted ? 'text-white' : 'text-heading'}`}>
            {title}
          </p>}
        {description &&
          <p style={{ transition: 'color .4s ease-in-out' }}
            className={`w-full h-full font-nunitoSansRegular ${active || highlighted ? 'text-[rgb(255,255,255,.9)]' : 'text-gray'} text-center overflow-hidden line-clamp-4`}>
            {description}
          </p>}
      </div>
    </div>
  );
};

const SuccessStory = ({ paragraphs }) => {
  return (
    <div className='flex flex-col w-full md:w-[40%] h-fit space-y-4 shadow-[inset_0_0_0_4px_rgb(209,235,249)] p-6 overflow-hidden rounded-lg'>
      <img src='/images/img_15.jpg' className='w-full rounded-lg' />
      <p className='font-nunitoSansBold text-[20px] text-heading'>Read Our Success Story for Inspiration</p>
      {paragraphs && paragraphs.length > 0 &&
        paragraphs.map((paragraph, i) =>
          <p key={i} className='w-full h-fit text-[16px]/[28px] text-gray font-nunitoSansRegular' >
            {paragraph}
          </p>
        )
      }
      <Button text='Contact us' />
    </div>
  );
};

const Statistics = () => {
  const statisticsRef = useRef(null);

  useEffect(() => {
    scrollCallbacks.push(() => {
      if (statisticsRef && statisticsRef.current) {
        let top = Math.round(statisticsRef.current.getBoundingClientRect().top);
        if (top < window.innerHeight) {
          let titles = statisticsRef.current.getElementsByClassName('title');
          for (let title of titles) {
            title.classList.remove('translate-y-full');;
          }
        }
      }
    })
  }, []);
  return (
    <div className='flex flex-col w-full h-fit'>
      <div ref={statisticsRef} style={{ backgroundImage: 'url(/images/img_18.jpg)' }}
        className='relative flex flex-col w-full h-[160px] xs:h-[177.59px] md:h-[184.8px] items-center px-4 xs:px-[8%] sm:px-[13%] md:px-[5%] xl:px-[13%] bg-center bg-cover overflow-hidden shrink-0'>
        <div style={{ background: 'linear-gradient(to right, #1566ad 0%, #1b9de3 100%)', opacity: '.8' }} className="absolute left-0 top-0 w-full h-full" />
        <div className='w-full h-full z-10 overflow-hidden'>
          <p style={{ transition: 'all 1s ease-in-out' }}
            className='flex title w-full h-full justify-center items-center text-center text-[28px] md:text-[34px] text-white font-nunitoSansBold translate-y-full'>
            You Always Get the Best Guidance
          </p>
        </div>
        <div className='hidden xs:block w-full h-[52px] shrink-0' />
      </div>
      <div className='w-full h-fit px-4 xs:px-[8%] sm:px-[13%] md:px-[5%] xl:px-[13%]'>
        <div className={`flex w-full h-fit md:h-[157.2px] px-8 pt-8 pb-12 md:pt-0 md:py-0 z-10 items-center bg-white shadow-lg shadow-gray-200 xs:-translate-y-[52px] shrink-0 rounded-lg`}>
          <div className='flex flex-col md:flex-row space-y-6 md:space-y-0 md:justify-between w-full h-fit overflow-hidden'>
            <Statistic statistic='6000' text='REGISTERED MEMBERS' />
            <Statistic statistic='116' text='ACTIVE DISTRICTS' />
            <Statistic statistic='35' text='STAFF MEMBERS' />
            <Statistic statistic='10' text='OUR PARTNERS' />
          </div>
        </div>
      </div>
    </div>

  );
};

const Statistic = ({ statistic, text }) => {
  const [startCounter, setStartCounter] = useState(false);
  const statisticRef = useRef(null);
  useEffect(() => {
    scrollCallbacks.push(() => {
      if (statisticRef && statisticRef.current) {
        let top = Math.round(statisticRef.current.getBoundingClientRect().top);
        if (top < window.innerHeight) {
          setStartCounter(true);
          let statistics = statisticRef.current.getElementsByClassName('statistic');
          for (let statistic of statistics) {
            statistic.classList.remove('translate-y-full');
          }
        }
      }
    })
  }, []);
  return (
    <div ref={statisticRef} className='w-fit h-fit overflow-hidden'>
      <div style={{ transition: 'all 1s ease-in-out' }}
        className='statistic flex flex-col translate-y-full'>
        <div className='relative flex items-center h-[72px] after:absolute after:bottom-[5px] after:left-0 after:w-[35px] after:h-0.5 after:bg-theme'>
          <p style={{ '--num': startCounter ? statistic : 0 }} className='number font-nunitoSansSemiBold text-[40px]/[40px]' />
        </div>
        <p className='font-nunitoSansextraBold text-sm text-[rgba(0,0,0,.8)] tracking-[1px]'>
          {text}
        </p>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <Section
      name="Our Best Services"
      description="We offer a comprehensive range of services designed to support clinical officers in their professional journey, from continuing education and research opportunities to career advancement and member welfare programs."
    >
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden ${X_PADDING}`}>
        <Service
          Icon={CIPortal}
          title='CPD Platform'
          description='Access accredited CPD programmes and lifelong learning opportunities.'
        />
        <Service
          Icon={CIMedicalInsurance}
          title='Health Insurance'
          description='Advocating for affordable and accessible health insurance for all Zambians.'
        />
        <Service
          Icon={CITrophy}
          title='Awards & Certificates'
          description='Recognizing achievements and excellence through professional awards and certifications.'
        />
        <Service
          Icon={CIResources}
          title='Resource Banking'
          description='Access a central hub for clinical guidelines, learning materials, and professional resources.'
        />
        <Service
          Icon={CISearch}
          title='Research Publication'
          description='Share and explore research that advances and promotes clinical practice.'
        />
        <Service
          Icon={CISuccess}
          title='Careers'
          description='Discover professional career pathways designed to help Clinical Officers advance their careers.'
        />
      </div>
    </Section>
  );
};

const Service = ({ Icon, title, description }) => {
  const [highlighted, setHighlighted] = useState(false);
  return (
    <div style={{ transition: 'all .2s linear' }}
      onMouseEnter={() => setHighlighted(true)}
      onMouseLeave={() => setHighlighted(false)}
      className={`relative grid-item flex flex-col w-full h-64 lg:h-74 space-y-2 items-center p-8 overflow-hidden rounded-lg transition-all duration-300 ${highlighted ? 'bg-[#1477c2] text-white shadow-md' : 'bg-[#eef7fd] text-[#16365c]'
        }`}
    >
      {Icon && <div className='flex mb-4 shrink-0'><Icon size={48} fill={highlighted ? 'white' : 'rgb(27,156,227)'} /></div>}
      {title &&
        <p style={{ transition: 'color .4s ease-in-out' }}
          className={`text-[20px] text-center font-nunitoSansBold ${highlighted ? 'text-white' : 'text-heading'}`}>
          {title}
        </p>
      }
      {description &&
        <p style={{ transition: 'color .4s ease-in-out' }}
          className={`w-full h-full text-[16px]/[28px] ${highlighted ? 'text-[rgb(255,255,255,.9)]' : 'text-gray'} font-nunitoSansRegular text-center overflow-hidden`}>
          {description}
        </p>
      }
    </div>
  );
};

const ContactUs = () => {
  const contactUsRef = useRef(null);

  useEffect(() => {
    scrollCallbacks.push(() => {
      if (contactUsRef && contactUsRef.current) {
        let top = Math.round(contactUsRef.current.getBoundingClientRect().top);
        if (top < window.innerHeight) {
          let titles = contactUsRef.current.getElementsByClassName('slide-in');
          for (let title of titles) {
            title.classList.remove('translate-y-full');
          }
        }
      }
    })
  }, []);
  return (
    <div ref={contactUsRef} style={{ backgroundImage: 'url(/images/img_55.jpg)' }}
      className='relative flex flex-col w-full h-[202px] md:h-[154px] items-center px-4 xs:px-[8%] sm:px-[13%] md:px-[5%] xl:px-[13%] bg-center bg-cover overflow-hidden shrink-0'>
      <div style={{ background: 'linear-gradient(to right, #1566ad 0%, #1b9de3 100%)', opacity: '.8' }} className="absolute left-0 top-0 w-full h-full" />
      <div style={{ transition: 'all 1s ease-in-out' }}
        className='slide-in flex flex-col md:flex-row w-full h-full justify-center md:justify-between py-4 space-y-3 md:space-y-0 md:space-x-8 items-center z-10 translate-y-full overflow-hidden'>
        <p className='flex w-fit justify-center items-center text-[28px]/[40px] md:text-[34px] text-white font-nunitoSansBold'>
          You Always Get the Best Guidance
        </p>
        <button style={{ transition: 'all .5s ease-in-out' }}
          onClick={() => { }}
          className='w-full md:w-fit h-fit px-16 py-4 font-normal text-theme font-nunitoSansRegular bg-white rounded-full cursor-pointer shrink-0 text-nowrap hover:bg-[#eef7fd] transition'>
          Contact Us
        </button>
      </div>
    </div>
  );
};

const MemberBenefits = () => {
  return (
    <Section
      name="Our Member Benefits"
      description="Join COAZ and gain access to exclusive benefits including legal representation, financial services, CPD platforms, mentorship programs, and networking platforms that enhance both your professional and personal growth."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
        <Benefit img='/images/img_67.png' bgColor='bg-gradient-to-t from-[rgb(27,156,227,.9)] to-[rgb(27,156,227,.7)]' text='High Quality Lab Coats Distribution' />
        <Benefit img='/images/img_56.png' bgColor='bg-[rgb(234,229,221)]' text='Advocacy & Representation' />
        <Benefit img='/images/img_65.png' bgColor='bg-gradient-to-t from-[rgb(201,130,1)] to-[rgb(254,187,1)]' text='Legal Representation' />
        <Benefit img='/images/img_62.png' bgColor='bg-gradient-to-b from-[rgb(234,229,221,.8)] to-[rgb(234,229,221,.2)]' text='Continuing Professional Development' />
        <Benefit img='/images/img_58.png' bgColor='bg-gradient-to-b from-[rgb(234,229,221,.8)] to-[rgb(234,229,221,.2)]' text='Loan & Financial Services' />
        <Benefit img='/images/img_57.png' bgColor='bg-gradient-to-b from-[rgb(35,35,33)] to-[rgb(118,126,133)]' text='Recognition & Awards' />
        <Benefit img='/images/img_59.png' bgColor='h-64 bg-[rgb(245,245,245)]' text='Networking Opportunities' />
        <Benefit img='/images/img_60.png' bgColor='bg-gradient-to-b from-[rgb(226,205,149,.8)] to-[rgb(rgb(226,205,149,.7)]' text='Mentorship Programs' />
      </div>
    </Section>
  );
};

const Benefit = ({ img, bgColor, text }) => {
  const [highlighted, setHighlighted] = useState(false);
  return (
    <div className={`w-full h-[300px] ${bgColor} rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300`}>
      <div style={{ backgroundImage: `url(${img})` }} className={`relative w-full h-full bg-center bg-contain bg-no-repeat`}>
        <div style={{ transition: 'all .3s ease-in-out' }} className={`absolute left-0 top-0 w-full h-full bg-theme ${highlighted ? 'opacity-90' : 'md:opacity-0 opacity-40'}`} />
        <div onMouseEnter={() => setHighlighted(true)}
          onMouseLeave={() => setHighlighted(false)}
          className='absolute flex left-0 top-0 w-full h-full items-center justify-center'>
          <div style={{ transition: 'all .3s ease-in-out' }} className={`flex flex-col p-6 space-y-4 items-center justify-center ${highlighted ? 'opacity-100' : 'md:opacity-0 opacity-100'}`}>
            <p className='w-full h-fit text-[28px]/[40px] text-white font-nunitoSansSemiBold text-center cursor-pointer'>
              {text}
            </p>
            <p className='w-full h-fit text-center text-[12px] text-white tracking-[2px] font-nunitoSansSemiBold'>PAID MEMBERS ONLY</p>
            <button style={{ transition: 'all .3s ease-in-out' }}
              className={`absolute flex items-center justify-center w-[50px] h-[50px] right-0 bottom-0 bg-white hover:bg-theme cursor-pointer rounded-l-lg ${highlighted ? 'opacity-100' : 'opacity-0'}`}>
              <MdSubdirectoryArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Register = () => {
  const registerRef = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    scrollCallbacks.push(() => {
      if (registerRef && registerRef.current) {
        const rect = registerRef.current.getBoundingClientRect();
        const top = Math.round(rect.top);
        const range = window.innerHeight + Math.round(rect.height);
        const view = window.innerHeight - top;
        if (view > 0 && view < range && imgRef.current) {
          let imgTop = ((view - window.innerHeight) / range) * 100;
          imgRef.current.style.top = imgTop + '%';
        }
      }
    })
  }, []);
  return (
    <div ref={registerRef} className='relative flex w-full h-fit overflow-hidden'>
      <div ref={imgRef} className='absolute flex left-0 w-full h-[112vh] bg-[url(/public/images/img_46.jpg)] bg-cover bg-center' />
      <div style={{ background: 'linear-gradient(to right, rgba(21,102,173) 0%, rgba(27,157,227,.8) 100%)' }}
        className='flex flex-row-reverse w-full h-fit py-12 lg:py-24 px-4 xs:px-[8%] sm:px-[13%] md:px-[5%] xl:px-[13%] z-10'>
        <div className='flex flex-col w-full md:w-1/2 h-full justify-center space-y-6 overflow-hidden'>
          <div className='flex flex-col w-full h-fit space-y-6'>
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "https://auth.coaz.org/_register";
              }}
              className='text-[34px] text-white font-nunitoSansBold text-left'>
              Register with Us
            </button>
            <p className='text-[rgba(255,255,255,.9)] font-nunitoSansRegular'>
              Become part of a growing network of Clinical Officers shaping the future of healthcare in Zambia. Register with COAZ to stay connected, enhance your professional development, access valuable resources, and contribute to the advancement of the profession.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const News = () => {
  const [latestNews,setLatestNews] = useState([]);
  const {version,news} = useContext(GlobalContext);
  const {request,download} = client;

  useEffect(() => {
    setLatestNews(news.slice(0, 3));

  }, [news]);


  return (
    <Section 
      name="Our Latest News"
      description="Stay informed with the latest updates from the Clinical Officers' Association of Zambia. We share important 
      announcements, professional development opportunities, policy updates, healthcare initiatives, and highlights from our 
      events and advocacy efforts."
    >
      <NewsItems newsItems={latestNews}/>
    </Section>
  )
}